/**
 * BlogNotifyModal — single-click "Send notification email" UI for the
 * agency blog admin.
 *
 * Flow:
 *   1. Admin opens the modal from /admin/blog → "Notify subscribers"
 *      button on a published post row.
 *   2. We hit GET /api/admin/blog/posts/:slug/notify/preview to fetch
 *      live recipient count, last-notified-at, and the four EmailOctopus
 *      field values that will be merged into the email template.
 *   3. Admin reviews the populated fields, clicks "Send to N
 *      subscribers" → POST /api/admin/blog/posts/:slug/notify.
 *   4. Backend fans out: batch-updates the 4 fields on every blog-
 *      subscribe contact, then queues the "Started via API" automation
 *      for each. EmailOctopus renders + sends one personalised email
 *      per subscriber, with CAN-SPAM unsubscribe + business-address
 *      footer appended automatically.
 *   5. Modal shows the delivery report (recipients, queued, failed).
 *
 * No HTML, no subject, no URLs ever touched by the admin.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import {
    Loader2,
    Mail,
    CheckCircle2,
    AlertTriangle,
    X,
    ExternalLink,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Auth bearer is attached globally by the axios interceptor in
// /app/frontend/src/lib/auth.js — no per-call header logic needed here.

function fmtDateTime(iso) {
    if (!iso) return null;
    try {
        return new Date(iso).toLocaleString();
    } catch {
        return iso;
    }
}

function ConfigMissingNotice({ config }) {
    // Surface a short, actionable checklist when the EmailOctopus
    // setup isn't complete yet. The user needs to know exactly which
    // env var is missing so they can fix it without guessing.
    const items = [
        { key: "api_key_set", label: "EMAILOCTOPUS_API_KEY" },
        { key: "list_id_set", label: "EmailOctopus list (auto-discovered or EMAILOCTOPUS_LIST_ID)" },
        {
            key: "automation_id_set",
            label: "EMAILOCTOPUS_BLOG_NOTIFY_AUTOMATION_ID",
        },
    ];
    return (
        <div className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-4 text-sm text-amber-100">
            <p className="flex items-center gap-2 font-medium">
                <AlertTriangle className="h-4 w-4" />
                EmailOctopus is not fully configured yet.
            </p>
            <ul className="mt-3 space-y-1.5 text-amber-100/85">
                {items.map((it) => (
                    <li key={it.key} className="flex items-center gap-2">
                        <span
                            className={
                                "inline-block h-2 w-2 rounded-full " +
                                (config[it.key] ? "bg-emerald-400" : "bg-rose-400")
                            }
                        />
                        <code className="text-xs">{it.label}</code>
                        {config[it.key] ? null : (
                            <span className="text-xs text-rose-200">missing</span>
                        )}
                    </li>
                ))}
            </ul>
            <p className="mt-3 text-xs text-amber-100/70">
                Add the missing env var(s) in <code>/app/backend/.env</code> and
                restart the backend.
            </p>
        </div>
    );
}

function FieldRow({ label, value }) {
    return (
        <div className="flex flex-col gap-1 border-t border-white/5 py-2 text-xs sm:flex-row sm:items-baseline sm:gap-3">
            <span className="w-44 shrink-0 uppercase tracking-[0.2em] text-brand-muted">
                {label}
            </span>
            <span className="break-all font-mono text-[11px] text-brand-ivory/90">
                {value || (
                    <span className="text-brand-muted/60">(empty)</span>
                )}
            </span>
        </div>
    );
}

export default function BlogNotifyModal({ slug, onClose }) {
    const [preview, setPreview] = useState(null);
    const [loading, setLoading] = useState(true);
    const [sending, setSending] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);
    const [force, setForce] = useState(false);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(null);
        axios
            .get(
                `${API}/admin/blog/posts/${encodeURIComponent(slug)}/notify/preview`
            )
            .then((res) => {
                if (!cancelled) setPreview(res.data);
            })
            .catch((e) => {
                if (!cancelled)
                    setError(
                        e?.response?.data?.detail ||
                            "Failed to load notification preview."
                    );
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [slug]);

    const handleSend = async () => {
        setSending(true);
        setError(null);
        try {
            const res = await axios.post(
                `${API}/admin/blog/posts/${encodeURIComponent(slug)}/notify` +
                    (force ? "?force=true" : ""),
                {}
            );
            setResult(res.data);
        } catch (e) {
            setError(
                e?.response?.data?.detail ||
                    "Send failed — see backend logs for details."
            );
        } finally {
            setSending(false);
        }
    };

    return (
        <div
            data-testid="blog-notify-modal"
            className="fixed inset-0 z-[80] flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm"
            onClick={onClose}
        >
            <div
                onClick={(e) => e.stopPropagation()}
                className="relative max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl border border-[rgba(212,175,55,0.25)] bg-[#0b0e13] p-6 shadow-2xl sm:p-8"
            >
                <button
                    type="button"
                    onClick={onClose}
                    data-testid="blog-notify-modal-close"
                    className="absolute right-4 top-4 inline-flex h-8 w-8 items-center justify-center rounded-full border border-white/15 text-brand-muted hover:bg-white/5 hover:text-brand-ivory"
                    aria-label="Close"
                >
                    <X className="h-4 w-4" />
                </button>

                <p className="text-[10px] uppercase tracking-[0.3em] text-brand-goldSoft">
                    Notify subscribers
                </p>
                <h2 className="mt-2 font-serif text-2xl text-brand-ivory">
                    Send a blog notification
                </h2>

                {/* ----- Loading ----- */}
                {loading ? (
                    <div className="mt-8 flex items-center gap-2 text-brand-muted">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Loading preview…
                    </div>
                ) : null}

                {/* ----- Error loading preview ----- */}
                {!loading && error && !preview ? (
                    <div className="mt-6 rounded-lg border border-rose-500/40 bg-rose-500/5 p-4 text-sm text-rose-100">
                        {error}
                    </div>
                ) : null}

                {/* ----- Preview state ----- */}
                {!loading && preview && !result ? (
                    <div className="mt-6 space-y-5">
                        {!preview.config.ready ? (
                            <ConfigMissingNotice config={preview.config} />
                        ) : null}

                        <div className="rounded-lg border border-white/10 bg-[#10141b] p-4">
                            <p className="text-xs uppercase tracking-[0.2em] text-brand-muted">
                                Post
                            </p>
                            <p
                                className="mt-1 font-serif text-lg text-brand-ivory"
                                data-testid="blog-notify-modal-post-title"
                            >
                                {preview.post_title}
                            </p>
                            <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
                                <div>
                                    <p className="text-[10px] uppercase tracking-[0.2em] text-brand-muted">
                                        Recipients
                                    </p>
                                    <p
                                        className="mt-1 text-2xl font-semibold text-brand-gold"
                                        data-testid="blog-notify-modal-recipients"
                                    >
                                        {preview.recipients_count}
                                    </p>
                                    <p className="text-[10px] text-brand-muted">
                                        tagged <code>blog-subscribe</code>
                                    </p>
                                </div>
                                <div>
                                    <p className="text-[10px] uppercase tracking-[0.2em] text-brand-muted">
                                        Last sent
                                    </p>
                                    <p className="mt-1 text-sm text-brand-ivory/80">
                                        {fmtDateTime(preview.last_notified_at) || (
                                            <span className="text-brand-muted/60">
                                                Never
                                            </span>
                                        )}
                                    </p>
                                    {preview.last_notified_recipients ? (
                                        <p className="text-[10px] text-brand-muted">
                                            to {preview.last_notified_recipients}{" "}
                                            subscribers
                                        </p>
                                    ) : null}
                                </div>
                            </div>
                        </div>

                        <details className="rounded-lg border border-white/10 bg-[#10141b] p-4 text-sm">
                            <summary className="cursor-pointer text-xs uppercase tracking-[0.2em] text-brand-muted">
                                Email-template merge fields (auto-populated)
                            </summary>
                            <div className="mt-3 space-y-1">
                                <FieldRow
                                    label="LatestPostTitle"
                                    value={preview.fields_preview.LatestPostTitle}
                                />
                                <FieldRow
                                    label="LatestPostUrl"
                                    value={preview.fields_preview.LatestPostUrl}
                                />
                                <FieldRow
                                    label="LatestPostExcerpt"
                                    value={preview.fields_preview.LatestPostExcerpt}
                                />
                                <FieldRow
                                    label="LatestPostHeroUrl"
                                    value={preview.fields_preview.LatestPostHeroUrl}
                                />
                            </div>
                        </details>

                        {preview.last_notified_at ? (
                            <label className="flex items-start gap-2 text-xs text-brand-muted">
                                <input
                                    type="checkbox"
                                    data-testid="blog-notify-modal-force"
                                    checked={force}
                                    onChange={(e) => setForce(e.target.checked)}
                                    className="mt-0.5 h-4 w-4 rounded border-white/20 bg-transparent text-brand-gold focus:ring-brand-gold"
                                />
                                <span>
                                    Re-send even though this post was notified
                                    within the last 24 hours (bypasses the
                                    double-send guard).
                                </span>
                            </label>
                        ) : null}

                        {error ? (
                            <div className="rounded-lg border border-rose-500/40 bg-rose-500/5 p-3 text-xs text-rose-100">
                                {error}
                            </div>
                        ) : null}

                        <div className="flex flex-col items-stretch gap-3 sm:flex-row-reverse sm:justify-start">
                            <button
                                type="button"
                                onClick={handleSend}
                                disabled={
                                    sending ||
                                    !preview.config.ready ||
                                    preview.recipients_count === 0
                                }
                                data-testid="blog-notify-modal-send"
                                className="inline-flex items-center justify-center gap-2 rounded-full bg-brand-gold px-6 py-3 text-sm font-semibold uppercase tracking-[0.18em] text-[#0b0e13] transition-all hover:bg-brand-gold2 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                                {sending ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                    <Mail className="h-4 w-4" />
                                )}
                                {sending
                                    ? "Sending…"
                                    : `Send to ${preview.recipients_count} subscriber${preview.recipients_count === 1 ? "" : "s"}`}
                            </button>
                            <button
                                type="button"
                                onClick={onClose}
                                disabled={sending}
                                className="inline-flex items-center justify-center rounded-full border border-white/20 px-5 py-3 text-xs uppercase tracking-[0.18em] text-brand-muted hover:bg-white/5"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                ) : null}

                {/* ----- Result state ----- */}
                {result ? (
                    <div
                        className="mt-6 space-y-4"
                        data-testid="blog-notify-modal-result"
                    >
                        {result.ok && !result.failed ? (
                            <div className="flex items-start gap-3 rounded-lg border border-emerald-500/40 bg-emerald-500/5 p-4 text-sm text-emerald-100">
                                <CheckCircle2 className="h-5 w-5 shrink-0" />
                                <div>
                                    <p className="font-medium">
                                        Notification queued through EmailOctopus.
                                    </p>
                                    <p className="mt-1 text-emerald-100/80">
                                        {result.queued} of {result.recipients}{" "}
                                        subscribers were queued.
                                    </p>
                                    <p className="mt-2 text-xs text-emerald-100/70">
                                        EmailOctopus will deliver within a few
                                        minutes. Open / click tracking shows up in
                                        the EmailOctopus dashboard.
                                    </p>
                                </div>
                            </div>
                        ) : result.code === "partial_failure" ||
                          (result.failed > 0 && result.queued > 0) ? (
                            // Mixed outcome — surface explicit "X delivered /
                            // Y failed" so the admin can't read a green tick
                            // over a real delivery failure.
                            <div className="flex items-start gap-3 rounded-lg border border-amber-500/50 bg-amber-500/5 p-4 text-sm text-amber-100">
                                <AlertTriangle className="h-5 w-5 shrink-0" />
                                <div>
                                    <p className="font-medium">
                                        Partial send — {result.queued} of{" "}
                                        {result.recipients} delivered.
                                    </p>
                                    <p className="mt-1 text-amber-100/85">
                                        {result.failed} subscriber
                                        {result.failed === 1 ? "" : "s"} were
                                        rejected by EmailOctopus. See the
                                        per-contact errors below.
                                    </p>
                                    <p className="mt-2 text-xs text-amber-100/70">
                                        Common cause: the EmailOctopus
                                        automation&apos;s <em>Allow contacts to
                                        repeat</em> setting is disabled, so
                                        subscribers who completed this
                                        automation on a previous post are
                                        permanently locked out. Flip the toggle
                                        and re-send with{" "}
                                        <code>force=true</code>.
                                    </p>
                                </div>
                            </div>
                        ) : (
                            <div className="flex items-start gap-3 rounded-lg border border-rose-500/40 bg-rose-500/5 p-4 text-sm text-rose-100">
                                <AlertTriangle className="h-5 w-5 shrink-0" />
                                <div>
                                    <p className="font-medium">
                                        {result.code === "already_notified"
                                            ? "Already sent recently."
                                            : result.code === "not_configured"
                                              ? "EmailOctopus not configured."
                                              : result.code === "send_failed"
                                                ? "Every subscriber was rejected by EmailOctopus."
                                                : "Send did not complete."}
                                    </p>
                                    <p className="mt-1 text-rose-100/85">
                                        {result.detail ||
                                            `${result.failed || 0} of ${result.recipients || 0} subscribers failed. See the errors below.`}
                                    </p>
                                </div>
                            </div>
                        )}

                        {result.errors && result.errors.length > 0 ? (
                            <details className="rounded-lg border border-white/10 bg-[#10141b] p-3 text-xs">
                                <summary className="cursor-pointer text-brand-muted">
                                    First {result.errors.length} errors
                                </summary>
                                <ul className="mt-2 space-y-1 text-rose-200/80">
                                    {result.errors.map((e, i) => (
                                        <li key={i} className="font-mono text-[10px]">
                                            {e}
                                        </li>
                                    ))}
                                </ul>
                            </details>
                        ) : null}

                        <div className="flex items-center justify-between">
                            <a
                                href="https://emailoctopus.com/campaigns"
                                target="_blank"
                                rel="noreferrer"
                                className="inline-flex items-center gap-1 text-xs text-brand-muted hover:text-brand-ivory"
                            >
                                Open EmailOctopus dashboard
                                <ExternalLink className="h-3 w-3" />
                            </a>
                            <button
                                type="button"
                                onClick={onClose}
                                data-testid="blog-notify-modal-done"
                                className="inline-flex items-center justify-center rounded-full bg-brand-gold px-5 py-2 text-xs font-semibold uppercase tracking-[0.18em] text-[#0b0e13] hover:bg-brand-gold2"
                            >
                                Done
                            </button>
                        </div>
                    </div>
                ) : null}
            </div>
        </div>
    );
}
